import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Crown,
  Instagram,
  Sparkles,
} from "lucide-react";
import { CEO_SPOTLIGHT } from "../lib/site";

const LEADERSHIP_TILES = [
  {
    icon: Crown,
    label: "Role",
    value: CEO_SPOTLIGHT.role,
  },
  {
    icon: Sparkles,
    label: "Brand Focus",
    value: "Luxury wedding performance, polished execution and premium guest experience.",
  },
  {
    icon: BadgeCheck,
    label: "Leadership Style",
    value: "Creative direction backed by dependable show discipline.",
  },
];

export const LeadershipSpotlight = () => {
  return (
    <section
      data-testid="leadership-spotlight-section"
      className="relative overflow-hidden bg-[#070707] py-24 md:py-32 lg:py-40"
    >
      <div className="pointer-events-none absolute left-[-8%] top-[12%] h-[300px] w-[300px] rounded-full bg-[#d4af37]/8 blur-3xl" />
      <div className="pointer-events-none absolute right-[-10%] bottom-[12%] h-[360px] w-[360px] rounded-full bg-[#4b1f12]/12 blur-3xl" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-6 md:px-10 lg:grid-cols-12 lg:gap-16 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.85 }}
          className="lg:col-span-5"
        >
          <div className="relative overflow-hidden border border-[#d4af37]/18 bg-[#120d0b] p-3 shadow-[0_34px_100px_-46px_rgba(212,175,55,0.34)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(212,175,55,0.1),transparent_42%)]" />
            <img
              src={CEO_SPOTLIGHT.image}
              alt={CEO_SPOTLIGHT.name}
              className="relative h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-[linear-gradient(180deg,transparent,rgba(7,7,7,0.88))]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, delay: 0.05 }}
          className="lg:col-span-7"
        >
          <div className="overline">— Leadership</div>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.04] md:text-5xl lg:text-6xl">
            Meet <em className="gold-gradient-text not-italic">Anmol Baluja</em>, the CEO guiding the AB Events standard.
          </h2>

          <div className="gold-hairline my-8 max-w-xs" />

          <div className="max-w-3xl space-y-5 text-base font-light leading-relaxed text-white/66 md:text-lg">
            <p>{CEO_SPOTLIGHT.intro}</p>
            <p>{CEO_SPOTLIGHT.description}</p>
          </div>

          <blockquote className="mt-10 border-l border-[#d4af37]/40 bg-[linear-gradient(90deg,rgba(212,175,55,0.08),rgba(255,255,255,0.01))] px-6 py-5 font-serif text-xl font-light leading-relaxed text-white/86 md:text-2xl">
            “{CEO_SPOTLIGHT.quote}”
          </blockquote>

          <div className="mt-10 grid gap-3 md:grid-cols-3">
            {LEADERSHIP_TILES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="border border-white/10 bg-white/[0.03] p-5"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
                    <Icon size={14} />
                    {item.label}
                  </div>
                  <div className="mt-3 text-sm font-light leading-relaxed text-white/72">
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 border border-white/10 bg-[linear-gradient(180deg,rgba(212,175,55,0.06)_0%,rgba(10,10,10,0.94)_100%)] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
              Leadership pillars
            </div>
            <div className="mt-5 grid gap-3">
              {CEO_SPOTLIGHT.pillars.map((point) => (
                <div
                  key={point}
                  className="flex items-start gap-3 border-t border-white/8 pt-4 first:border-t-0 first:pt-0"
                >
                  <div className="mt-0.5 text-[#d4af37]">✦</div>
                  <div className="text-sm font-light leading-relaxed text-white/68 md:text-base">
                    {point}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a
              href="#bookings"
              className="gold-button inline-flex items-center gap-2 rounded-sm px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em]"
            >
              For Enquiry <ArrowRight size={14} />
            </a>
            <a
              href={CEO_SPOTLIGHT.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-sm border border-white/18 bg-white/[0.03] px-6 py-4 text-[11px] font-medium uppercase tracking-[0.24em] text-white/78 transition-colors duration-300 hover:border-[#d4af37]/45 hover:text-white"
            >
              <Instagram size={14} />
              View Instagram
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadershipSpotlight;
